import React, { useCallback, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useTranslation } from "react-i18next";
import { Label, Select } from "flowbite-react";
import { StatusBadge } from "../../atoms/StatusBadge";
import { Spinner } from "../../atoms/Spinner";
import { HiClock, HiOutlinePencil, HiUserGroup } from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { toast } from "react-toastify";
import parse from "html-react-parser";

import { CustomInput } from "../../atoms/CustomInput";
import { CustomButton } from "../../atoms/CustomButton";
import UnlockBadge from "../../atoms/UnlockBadge";
import { createTicketSchema } from "./schemas";
import {
  CURRENCIES,
  DEFAULT_TICKET_VALUES,
  T_PREFIX,
  SALE_DATE_MIN_DURATION_MINUTES,
} from "./constants";
import { TicketFormData, TicketFormProps } from "./types";
import { PlanTier, usePermissions } from "../../../utils/permissions";
import useAuth from "../../../hooks/useAuth";

export const TicketForm: React.FC<TicketFormProps> = ({
  isEditing,
  onSubmit,
  onCancel,
  defaultValues,
  onShowPaidTicketUpgrade,
  groups,
  isLoadingGroups,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const hasPaymentLinkAccess = permissions.isAtLeastTier(PlanTier.ENTERPRISE);

  const ticketSchema = useMemo(() => createTicketSchema(t), [t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<TicketFormData>({
    resolver: joiResolver(ticketSchema),
    defaultValues: { ...DEFAULT_TICKET_VALUES, ...defaultValues },
  });

  const price = useWatch({ control, name: "price" });
  const currency = useWatch({ control, name: "currency" });
  const showRemainingTickets = useWatch({
    control,
    name: "show_remaining_tickets",
  });
  const groupIdentification = useWatch({
    control,
    name: "group_identification",
  });

  const isFreeTicket = !price || price === 0;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handlePaidTicketToggle = useCallback(
    (checked: boolean) => {
      setValue("price", checked ? 1 : 0);
    },
    [setValue]
  );

  const validateAndSubmit = useCallback(
    (data: TicketFormData) => {
      if (data.min_per_order > data.max_per_order) {
        toast.error(t(`${T_PREFIX}.validationErrors.minGreaterThanMax`));
        return;
      }

      if (data.quantity_total && data.max_per_order > data.quantity_total) {
        toast.error(t(`${T_PREFIX}.validationErrors.maxPerOrderExceedsTotal`));
        return;
      }

      if (data.sale_start_date && data.sale_end_date) {
        const startDate = new Date(data.sale_start_date);
        const endDate = new Date(data.sale_end_date);
        const diffInMinutes =
          (endDate.getTime() - startDate.getTime()) / (1000 * 60);

        if (endDate <= startDate) {
          toast.error(t(`${T_PREFIX}.validationErrors.saleEndAfterStart`));
          return;
        }

        if (diffInMinutes < SALE_DATE_MIN_DURATION_MINUTES) {
          toast.error(t(`${T_PREFIX}.validationErrors.saleEndMinimumDuration`));
          return;
        }
      }

      onSubmit(data);
    },
    [onSubmit, t]
  );

  return (
    <div
      className="bg-gray-50 border border-gray-200 p-2 md:p-4 rounded-lg space-y-2 md:space-y-3 mb-2 md:mb-4"
      role="region"
      aria-label={isEditing ? "Edit ticket form" : "New ticket form"}
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-1 md:mb-2">
        {isEditing ? (
          <HiOutlinePencil
            size={16}
            className="text-gray-400"
            aria-hidden="true"
          />
        ) : (
          <HiTicket size={16} className="text-gray-400" aria-hidden="true" />
        )}
        <span>
          {isEditing
            ? t(`${T_PREFIX}.ticketForm.editingTitle`)
            : t(`${T_PREFIX}.ticketForm.newTitle`)}
        </span>
      </div>

      <CustomInput
        name="name"
        control={control}
        error={errors.name}
        helperText={errors.name?.message}
        label={t(`${T_PREFIX}.ticketForm.nameLabel`) as string}
        placeholder={t(`${T_PREFIX}.ticketForm.namePlaceholder`) as string}
        maxLength={50}
      />

      <div className="bg-white border border-gray-200 rounded-lg p-2 md:p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_paid_ticket"
              checked={!isFreeTicket}
              disabled={!hasPaymentLinkAccess}
              onChange={(e) =>
                hasPaymentLinkAccess && handlePaidTicketToggle(e.target.checked)
              }
              className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
            />
            <Label
              htmlFor="is_paid_ticket"
              value={t(`${T_PREFIX}.ticketForm.paidTicket`) as string}
              className="cursor-pointer"
            />
            {!hasPaymentLinkAccess && onShowPaidTicketUpgrade && (
              <UnlockBadge onClick={onShowPaidTicketUpgrade} className="ml-2" />
            )}
          </div>
        </div>
      </div>

      {isFreeTicket ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2 md:p-4">
          <div className="flex items-center gap-2 mb-2 md:mb-3">
            <StatusBadge color="success" size="sm">
              {t(`${T_PREFIX}.ticketForm.freeTicketBadge`)}
            </StatusBadge>
            <span className="hidden md:inline text-sm text-green-700">
              {t(`${T_PREFIX}.ticketForm.freeTicketNote`)}
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:gap-4">
            <Label
              htmlFor="ticket_quantity"
              value={t(`${T_PREFIX}.ticketForm.quantityLabelFree`) as string}
              className="md:whitespace-nowrap md:min-w-fit"
            />
            <div className="md:flex-1">
              <CustomInput
                name="quantity_total"
                control={control}
                type="number"
                error={errors.quantity_total}
                helperText={errors.quantity_total?.message}
                placeholder={
                  t(`${T_PREFIX}.ticketForm.quantityPlaceholder`) as string
                }
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2 md:space-y-0 md:grid md:grid-cols-3 md:gap-3">
          <CustomInput
            name="price"
            control={control}
            type="number"
            error={errors.price}
            helperText={errors.price?.message}
            label={t(`${T_PREFIX}.ticketForm.priceLabel`) as string}
            placeholder={t(`${T_PREFIX}.ticketForm.pricePlaceholder`) as string}
          />
          <div>
            <Label
              htmlFor="ticket_currency"
              value={t(`${T_PREFIX}.ticketForm.currencyLabel`) as string}
              className="text-gray-500 text-xs font-normal leading-none"
            />
            <Select
              id="ticket_currency"
              value={currency || "USD"}
              onChange={(e) => setValue("currency", e.target.value)}
              className="w-full"
              aria-label="Select currency"
            >
              {CURRENCIES.map((curr) => (
                <option key={curr} value={curr}>
                  {curr}
                </option>
              ))}
            </Select>
          </div>
          <CustomInput
            name="quantity_total"
            control={control}
            type="number"
            error={errors.quantity_total}
            helperText={errors.quantity_total?.message}
            label={t(`${T_PREFIX}.ticketForm.quantityLabel`) as string}
            placeholder={
              t(`${T_PREFIX}.ticketForm.quantityPlaceholder`) as string
            }
          />
        </div>
      )}

      <div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 md:p-3 mb-2 md:mb-3">
          <div className="flex items-start gap-2">
            <HiClock
              className="text-blue-600 mt-0.5 flex-shrink-0"
              size={16}
              aria-hidden="true"
            />
            <div className="text-xs text-blue-800">
              {parse(
                t(`${T_PREFIX}.ticketForm.timezoneNote`, {
                  timezone: userTimezone,
                }) as string
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
          <CustomInput
            name="sale_start_date"
            control={control}
            type="datetime-local"
            error={errors.sale_start_date}
            helperText={errors.sale_start_date?.message}
            label={
              t(`${T_PREFIX}.ticketForm.saleStartLabel`, {
                timezone: userTimezone,
              }) as string
            }
            placeholder={
              t(`${T_PREFIX}.ticketForm.saleStartPlaceholder`) as string
            }
          />
          <CustomInput
            name="sale_end_date"
            control={control}
            type="datetime-local"
            error={errors.sale_end_date}
            helperText={errors.sale_end_date?.message}
            label={
              t(`${T_PREFIX}.ticketForm.saleEndLabel`, {
                timezone: userTimezone,
              }) as string
            }
            placeholder={
              t(`${T_PREFIX}.ticketForm.saleEndPlaceholder`) as string
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
        <CustomInput
          name="min_per_order"
          control={control}
          type="number"
          error={errors.min_per_order}
          helperText={errors.min_per_order?.message}
          label={t(`${T_PREFIX}.ticketForm.minPerOrderLabel`) as string}
          placeholder="1"
        />
        <CustomInput
          name="max_per_order"
          control={control}
          type="number"
          error={errors.max_per_order}
          helperText={errors.max_per_order?.message}
          label={t(`${T_PREFIX}.ticketForm.maxPerOrderLabel`) as string}
          placeholder="2"
        />
      </div>

      <div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="show_remaining_ticket"
            checked={showRemainingTickets}
            onChange={(e) =>
              setValue("show_remaining_tickets", e.target.checked)
            }
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <Label
            htmlFor="show_remaining_ticket"
            value={t(`${T_PREFIX}.ticketForm.showRemainingTickets`) as string}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1 ml-5 md:ml-6">
          {t(`${T_PREFIX}.ticketForm.showRemainingTicketsHelp`)}
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 md:p-3">
        <div className="flex items-start gap-2 mb-2">
          <HiUserGroup
            className="text-gray-500 mt-0.5 flex-shrink-0"
            size={16}
            aria-hidden="true"
          />
          <div>
            <Label
              htmlFor="ticket_group"
              value={t(`${T_PREFIX}.ticketForm.groupLabel`) as string}
              className="font-semibold text-gray-700"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t(`${T_PREFIX}.ticketForm.groupHelp`)}
            </p>
          </div>
        </div>
        <div className="relative">
          {isLoadingGroups ? (
            <div className="flex items-center gap-2 p-2">
              <Spinner size="sm" />
              <span className="text-sm text-gray-500">
                {t(`${T_PREFIX}.ticketForm.loadingGroups`)}
              </span>
            </div>
          ) : (
            <Select
              id="ticket_group"
              value={groupIdentification || ""}
              onChange={(e) => setValue("group_identification", e.target.value)}
              className="w-full"
              aria-label={t(`${T_PREFIX}.ticketForm.selectGroup`) as string}
            >
              <option value="">
                {t(`${T_PREFIX}.ticketForm.noGroupSelected`)}
              </option>
              {groups?.map((group) => (
                <option
                  key={group.group_identification}
                  value={group.group_identification}
                >
                  {group.group_name}
                </option>
              ))}
            </Select>
          )}
        </div>
      </div>

      <div className="flex gap-2 pt-1 md:pt-2 border-t">
        <CustomButton
          type="button"
          title={t(`${T_PREFIX}.actions.cancel`) as string}
          onClick={onCancel}
          variant="secondary"
        />
        <CustomButton
          type="button"
          title={
            isEditing
              ? (t(`${T_PREFIX}.actions.applyTicketType`) as string)
              : (t("common.add") as string)
          }
          onClick={handleSubmit(validateAndSubmit)}
          variant="primary"
        />
      </div>
    </div>
  );
};

TicketForm.displayName = "TicketForm";
